import api from "./api";

// Geocode a free-form address through our backend (avoids Nominatim browser blocking + CORS).
// Returns {lat, lng, display_name, approximate, precise, precision_label,
//          is_full_address, missing_address_parts[], components} or null.
export async function geocodeAddress(address) {
  if (!address || address.trim().length < 3) return null;
  try {
    const { data } = await api.get("/geocode", { params: { q: address } });
    if (!data || data.found === false) return null;
    return {
      lat: data.lat,
      lng: data.lng,
      display_name: data.display_name,
      approximate: Boolean(data.approximate),
      // True when the server's geocode returned a street-level match.
      // Default to `true` so older backends (pre-2026-05) don't trip
      // a false-positive warning during the deploy window.
      precise: data.precise !== false,
      precision_label: data.precision_label || "",
      // New (May 2026) — stricter "full address with ZIP" rule.
      // Defaults to `true` so older backends without the new flag don't
      // block save during the deploy gap.
      is_full_address: data.is_full_address !== false,
      missing_address_parts: Array.isArray(data.missing_address_parts)
        ? data.missing_address_parts
        : [],
      components: data.components || {},
    };
  } catch {
    return null;
  }
}

// Detect a coordinate pair pasted from Google Maps. Accepts every format
// Google Maps produces:
//   "40.0379, -76.3055"            decimal (comma)
//   "40.0379 -76.3055"             decimal (space only)
//   "(40.0379, -76.3055)"          decimal (parens)
//   "40.0379° N, 76.3055° W"       decimal with hemisphere letters
//   "40°02'16.4\"N 76°18'19.8\"W"  degrees/minutes/seconds (DMS)
// Returns {lat, lng} or null.
export function parseLatLng(text) {
  if (!text) return null;
  const cleaned = String(text).trim().replace(/^\(/, "").replace(/\)$/, "");

  // --- DMS: 40°02'16.4"N 76°18'19.8"W (minutes/seconds optional) ---
  const dmsPart = `(\\d{1,3})°\\s*(?:(\\d{1,2}(?:\\.\\d+)?)['’′]\\s*)?(?:(\\d{1,2}(?:\\.\\d+)?)["”″]\\s*)?([NSEW])`;
  const dmsRe = new RegExp(`^${dmsPart}[,\\s]+${dmsPart}$`, "i");
  const dm = cleaned.match(dmsRe);
  if (dm) {
    const toDec = (deg, min, sec, hemi) => {
      let v = parseFloat(deg) + (min ? parseFloat(min) / 60 : 0) + (sec ? parseFloat(sec) / 3600 : 0);
      if (/[SW]/i.test(hemi)) v = -v;
      return v;
    };
    const a = { v: toDec(dm[1], dm[2], dm[3], dm[4]), h: dm[4].toUpperCase() };
    const b = { v: toDec(dm[5], dm[6], dm[7], dm[8]), h: dm[8].toUpperCase() };
    const latPart = /[NS]/.test(a.h) ? a : b;
    const lngPart = /[EW]/.test(b.h) ? b : a;
    if (!/[NS]/.test(latPart.h) || !/[EW]/.test(lngPart.h)) return null;
    if (Math.abs(latPart.v) > 90 || Math.abs(lngPart.v) > 180) return null;
    return { lat: latPart.v, lng: lngPart.v };
  }

  // --- Decimal, with optional ° and hemisphere letters ---
  const decRe = /^(-?\d{1,3}(?:\.\d+)?)\s*°?\s*([NS])?\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*°?\s*([EW])?$/i;
  const m = cleaned.match(decRe);
  if (!m) return null;
  let lat = parseFloat(m[1]);
  let lng = parseFloat(m[3]);
  if (m[2] && m[2].toUpperCase() === "S") lat = -Math.abs(lat);
  if (m[4] && m[4].toUpperCase() === "W") lng = -Math.abs(lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  // Integer-only pairs like "12, 34" are more likely a typo than coords.
  if (!cleaned.includes(".")) return null;
  return { lat, lng };
}

// Coords → street address via the backend reverse geocoder.
// Returns { address, inServiceArea } or null when nothing was found.
export async function reverseGeocode(lat, lng) {
  try {
    const { data } = await api.get("/geocode/reverse", { params: { lat, lng } });
    if (!data || data.found === false || !data.address) return null;
    return { address: data.address, inServiceArea: data.in_service_area !== false };
  } catch {
    return null;
  }
}
